import React from 'react'
import {
  AlignItems,
  Body2,
  Bold,
  Box,
  Col,
  Color,
  Display,
  Grid,
  Heading2,
  Heading3,
  Heading4,
  Icon,
  IconVariant,
  JustifyContent,
  Link,
  LinkVariant,
  Space,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { ContentstackImage } from '../ContentstackImage'
import { CtaLink, mapCmsCta } from '../../cta'
import type { CmsCtaIntentHandlers } from '../../cta'
import type { RichTextContent } from '../../types/contentstack'

import type { ImageTextColumnsFields } from './types'

export type ImageTextColumnsProps = ImageTextColumnsFields & {
  ctaIntentHandlers?: CmsCtaIntentHandlers
}

type OrderedColProps = React.ComponentProps<typeof Col> & {
  mobileOrder: number
}

type SpaceSize = React.ComponentProps<typeof Space>['size']

function BaseOrderedCol({ mobileOrder: _mobileOrder, ...props }: OrderedColProps) {
  return <Col {...props} />
}

const OrderedCol = withCitrusTheme(BaseOrderedCol, (theme, props: OrderedColProps) => {
  const breakpointToLarge = `@media (max-width: ${theme.tokens.breakpoints[2]})`
  return {
    [breakpointToLarge]: { order: props.mobileOrder },
  }
})

const RichTextLink = withCitrusTheme(Link, (theme) => ({
  color: theme.color(Color.Secondary_400),
  textDecorationLine: 'underline',
}))

type RteNode = {
  type?: string
  uid?: string
  text?: string
  children?: RteNode[]
  attrs?: {
    url?: string
    target?: string
  }
  bold?: boolean
  italic?: boolean
  underline?: boolean
}

function isLeaf(node: RteNode): node is RteNode & { text: string } {
  return typeof node.text === 'string'
}

function RichTextLeaf({ node }: { node: RteNode & { text: string } }) {
  let content: React.ReactNode = node.text
  if (node.bold) {
    content = <Bold>{content}</Bold>
  }
  if (node.italic) {
    content = <em>{content}</em>
  }
  if (node.underline) {
    content = <u>{content}</u>
  }
  return <>{content}</>
}

function RichTextChildren({ nodes }: { nodes: RteNode[] }) {
  return (
    <>
      {nodes.map((node, index) => {
        const key = node.uid ?? `node-${index}`
        if (isLeaf(node)) {
          return <RichTextLeaf key={key} node={node} />
        }
        if (node.type === 'a' && node.attrs?.url) {
          return (
            <RichTextLink key={key} variant={LinkVariant.None} href={node.attrs.url}>
              <RichTextChildren nodes={node.children ?? []} />
            </RichTextLink>
          )
        }
        return <RichTextChildren key={key} nodes={node.children ?? []} />
      })}
    </>
  )
}

function hasRichTextContent(node: RteNode): boolean {
  if (isLeaf(node)) {
    return node.text.trim().length > 0
  }

  return (node.children ?? []).some(hasRichTextContent)
}

function getRichTextParagraphs(body?: RichTextContent) {
  if (!body) {
    return []
  }
  const root = body as RteNode
  const blocks = root.children ?? []
  return blocks.filter(
    (block) =>
      (block.type === 'p' || (block.children?.length ?? 0) > 0) && hasRichTextContent(block),
  )
}

function RichTextParagraphs({
  gapSize = 6,
  paragraphs,
}: {
  gapSize?: SpaceSize
  paragraphs: RteNode[]
}) {
  if (paragraphs.length === 0) {
    return null
  }

  return (
    <>
      {paragraphs.map((paragraph, index) => (
        <React.Fragment key={paragraph.uid ?? `p-${index}`}>
          {index > 0 && <Space size={gapSize} />}
          <Body2 size={4} style={{ whiteSpace: 'pre-wrap' }}>
            <RichTextChildren nodes={paragraph.children ?? []} />
          </Body2>
        </React.Fragment>
      ))}
    </>
  )
}

function RichTextBody({ body }: { body?: RichTextContent }) {
  const paragraphs = getRichTextParagraphs(body)

  if (paragraphs.length === 0) {
    return null
  }

  return (
    <Box paddingBottom={6}>
      <RichTextParagraphs paragraphs={paragraphs} />
    </Box>
  )
}

function SectionHeading({ level, children }: { level: 'h2' | 'h3'; children: React.ReactNode }) {
  if (level === 'h2') {
    return <Heading2>{children}</Heading2>
  }
  return <Heading3>{children}</Heading3>
}

export function ImageTextColumns({
  heading,
  heading_level,
  image_position,
  image,
  image_mobile,
  image_alt,
  body,
  sub_sections_heading,
  sub_sections_body,
  sub_sections,
  closing_body,
  cta,
  ctaIntentHandlers,
}: ImageTextColumnsProps) {
  const ctaLink = mapCmsCta(cta, ctaIntentHandlers)
  const imageOnLeft = image_position === 'left'
  const hasSubSections = sub_sections && sub_sections.length > 0
  const hasSubSectionsIntro = sub_sections_heading || sub_sections_body
  const shouldAddSubSectionCtaSpace = ctaLink && hasSubSections && !closing_body
  const textMobileOrder = 1
  const imageMobileOrder = 2

  const TextColumn = (
    <OrderedCol size={[12, null, null, 6]} mobileOrder={textMobileOrder}>
      <Box paddingRight={imageOnLeft ? 0 : [0, null, null, 10]}>
        <SectionHeading level={heading_level}>{heading}</SectionHeading>
        <Space size={2} />
        <RichTextBody body={body} />
        {hasSubSectionsIntro && (
          <>
            {sub_sections_heading && <Heading3>{sub_sections_heading}</Heading3>}
            {sub_sections_body && <Body2 size={4}>{sub_sections_body}</Body2>}
            {hasSubSections && <Space size={3} />}
          </>
        )}
        {hasSubSections && (
          <Box>
            {sub_sections?.map((item) => {
              const metadataUid = (item as { _metadata?: { uid: string } })._metadata?.uid
              const richTextParagraphs = getRichTextParagraphs(item.body)
              const hasRichTextBody = richTextParagraphs.length > 0

              return (
                <Box
                  key={metadataUid ?? item.body?.uid ?? item.sub_heading ?? item.sub_body}
                  display={item.show_tick_icon ? Display.Flex : Display.Block}
                  paddingBottom={item.show_tick_icon ? 2 : 4}
                >
                  {item.show_tick_icon && (
                    <Box paddingRight={1}>
                      <Icon name="tick" variant={IconVariant.Confirmation} data-testid="tick" />
                    </Box>
                  )}
                  <Box>
                    {hasRichTextBody ? (
                      <RichTextParagraphs gapSize={2} paragraphs={richTextParagraphs} />
                    ) : hasSubSectionsIntro ? (
                      <Body2 size={4}>
                        <strong>{item.sub_heading}</strong> {item.sub_body}
                      </Body2>
                    ) : (
                      <>
                        <Heading4 size={6} color={Color.ColdGrey_800}>
                          {item.sub_heading}
                        </Heading4>
                        <Body2 size={4}>{item.sub_body}</Body2>
                      </>
                    )}
                  </Box>
                </Box>
              )
            })}
          </Box>
        )}
        {shouldAddSubSectionCtaSpace && <Space size={6} />}
        {closing_body && (
          <>
            <Space size={2} />
            <Box paddingBottom={6}>
              <Body2 size={4}>{closing_body}</Body2>
            </Box>
          </>
        )}
      </Box>
      {ctaLink && (
        <Box
          display={Display.Flex}
          justifyContent={[JustifyContent.Center, JustifyContent.FlexStart]}
          paddingBottom={[6, null, null, 0]}
        >
          <CtaLink cta={ctaLink} />
        </Box>
      )}
      <Space size={[6, null, null, 0]} />
    </OrderedCol>
  )

  const ImageColumn = (
    <OrderedCol
      size={[12, null, null, 6]}
      mobileOrder={imageMobileOrder}
      display={Display.Flex}
      alignItems={AlignItems.FlexStart}
      justifyContent={JustifyContent.Center}
    >
      <Box
        paddingRight={imageOnLeft ? [0, null, null, 10] : 0}
        maxWidth={[null, null, null, '600px']}
        width="100%"
      >
        <Box style={{ borderRadius: '8px', overflow: 'hidden' }}>
          <ContentstackImage
            alt={image_alt}
            fit="cover"
            sizes="(max-width: 1023px) 100vw, 600px"
            src={image.url}
            mobileSrc={image_mobile?.url}
            mobileMedia="(max-width: 1023px)"
            style={{ maxHeight: '457px' }}
            width="100%"
          />
        </Box>
      </Box>
    </OrderedCol>
  )

  return (
    <Box paddingV={[8, 12, 12, 16]} paddingH={[4, 6, 14]}>
      <Grid>
        {imageOnLeft ? ImageColumn : TextColumn}
        {imageOnLeft ? TextColumn : ImageColumn}
      </Grid>
    </Box>
  )
}
